import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  useWindowDimensions,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';
import LocationFilterDropdown, { FilterOption } from './LocationFilterDropdown';
import LocationFilterPanel from './LocationFilterPanel';

export interface LocationFilterBarProps {
  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchClear: () => void;

  // Filter state and options
  regionOptions: FilterOption[];
  marketOptions: FilterOption[];
  locationOptions: FilterOption[];
  departmentOptions: FilterOption[];
  orgOptions: FilterOption[];
  locationTypeOptions: FilterOption[];

  selectedRegions: FilterOption[];
  onRegionChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedMarkets: FilterOption[];
  onMarketChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedDepartments: FilterOption[];
  onDepartmentChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedOrgs: FilterOption[];
  onOrgChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedTpl: FilterOption[];
  onTplChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedUnionStatuses: FilterOption[];
  onUnionStatusChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedSizes: FilterOption[];
  onSizeChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedLocations: FilterOption[];
  onLocationChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedLocationTypes: FilterOption[];
  onLocationTypeChange: (value: FilterOption | FilterOption[] | null) => void;

  selectedStatuses: string[];
  onStatusChange: (value: string[]) => void;

  onClearAllFilters: () => void;

  // Status
  isLocationDisabled?: boolean;
  locationPlaceholder?: string;
  isLoadingRegions?: boolean;
  isLoadingMarkets?: boolean;
  isLoadingLocations?: boolean;
  compactMode?: boolean;
}

const LocationFilterBar: React.FC<LocationFilterBarProps> = ({
  // Search
  searchValue,
  onSearchChange,
  onSearchClear,

  // Filters
  regionOptions,
  marketOptions,
  locationOptions,
  departmentOptions,
  orgOptions,
  locationTypeOptions,
  selectedRegions,
  onRegionChange,
  selectedMarkets,
  onMarketChange,
  selectedDepartments,
  onDepartmentChange,
  selectedOrgs,
  onOrgChange,
  selectedTpl,
  onTplChange,
  selectedUnionStatuses,
  onUnionStatusChange,
  selectedSizes,
  onSizeChange,
  selectedLocations,
  onLocationChange,
  selectedLocationTypes,
  onLocationTypeChange,
  selectedStatuses,
  onStatusChange,
  onClearAllFilters,

  // Status
  isLocationDisabled = false,
  locationPlaceholder = 'Select...',
  isLoadingRegions = false,
  isLoadingMarkets = false,
  isLoadingLocations = false,
  compactMode = false,
}) => {
  const { width } = useWindowDimensions();
  const isMobileLayout = width < 768;
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const statusOptions: FilterOption[] = useMemo(
    () => [
      { id: 'Active', displayText: 'Active' },
      { id: 'Pending Activation', displayText: 'Pending Activation' },
      { id: 'Marked for Removal', displayText: 'Marked for Removal' },
      { id: 'Disabled', displayText: 'Disabled' },
    ],
    []
  );

  const selectedStatusObjects = useMemo(
    () => selectedStatuses
      .map(s => statusOptions.find(opt => opt.id === s))
      .filter((s): s is FilterOption => !!s),
    [selectedStatuses, statusOptions]
  );

  const tplOptions: FilterOption[] = useMemo(
    () => [
      { id: 'Yes', displayText: 'Yes' },
      { id: 'No', displayText: 'No' },
    ],
    []
  );

  const unionStatusOptions: FilterOption[] = useMemo(
    () => [
      { id: 'Union', displayText: 'Union' },
      { id: 'Non-Union', displayText: 'Non-Union' },
      { id: 'Unknown', displayText: 'Unknown' },
    ],
    []
  );

  const sizeOptions: FilterOption[] = useMemo(
    () => [
      { id: 'Small', displayText: 'Small' },
      { id: 'Medium', displayText: 'Medium' },
      { id: 'Large', displayText: 'Large' },
    ],
    []
  );

  const selectedTplObjects = useMemo(
    () => selectedTpl
      .map(s => tplOptions.find(opt => opt.id === s.id))
      .filter((s): s is FilterOption => !!s),
    [selectedTpl, tplOptions]
  );

  const selectedUnionStatusObjects = useMemo(
    () => selectedUnionStatuses
      .map(s => unionStatusOptions.find(opt => opt.id === s.id))
      .filter((s): s is FilterOption => !!s),
    [selectedUnionStatuses, unionStatusOptions]
  );

  const selectedSizeObjects = useMemo(
    () => selectedSizes
      .map(s => sizeOptions.find(opt => opt.id === s.id))
      .filter((s): s is FilterOption => !!s),
    [selectedSizes, sizeOptions]
  );

  const handleStatusChange = (value: FilterOption | FilterOption[] | null) => {
    if (Array.isArray(value)) {
      onStatusChange(value.map(v => String(v.id)));
    } else if (value) {
      onStatusChange([String(value.id)]);
    } else {
      onStatusChange([]);
    }
  };

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    if (selectedRegions.length > 0) count++;
    if (selectedMarkets.length > 0) count++;
    if (selectedDepartments.length > 0) count++;
    if (selectedOrgs.length > 0) count++;
    if (selectedTpl.length > 0) count++;
    if (selectedUnionStatuses.length > 0) count++;
    if (selectedSizes.length > 0) count++;
    if (selectedLocations.length > 0) count++;
    if (selectedLocationTypes.length > 0) count++;
    if (selectedStatuses.length > 0) count++;
    if (searchValue.trim() !== '') count++;
    return count;
  }, [selectedRegions, selectedMarkets, selectedDepartments, selectedOrgs, selectedTpl, selectedUnionStatuses, selectedSizes, selectedLocations, selectedLocationTypes, selectedStatuses, searchValue]);

  const hasActiveFilters = appliedFilterCount > 0;

  const handleMobileFilterClose = () => {
    setMobileFilterOpen(false);
  };

  const handleMobileFilterApply = () => {
    setMobileFilterOpen(false);
  };

  const handleMobileFilterReset = () => {
    onClearAllFilters();
    setMobileFilterOpen(false);
  };

  // Desktop view
  if (!isMobileLayout) {
    // Compact mode: render filters only (no search bar wrapper)
    if (compactMode) {
      return (
        <View style={styles.compactFilterControls}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.compactFilterRow}
          >
            <View style={styles.compactField}>
              <Text style={styles.compactFieldLabel}>DEPARTMENT</Text>
              <LocationFilterDropdown
                label="Department"
                options={departmentOptions}
                value={selectedDepartments.length > 0 ? selectedDepartments : null}
                onChange={onDepartmentChange}
                isMulti
                showLabelInTrigger={false}
                size="compact"
                placeholder="All"
              />
            </View>
            <View style={styles.compactField}>
              <Text style={styles.compactFieldLabel}>ORG</Text>
              <LocationFilterDropdown
                label="Org"
                options={orgOptions}
                value={selectedOrgs.length > 0 ? selectedOrgs : null}
                onChange={onOrgChange}
                isMulti
                showLabelInTrigger={false}
                size="compact"
                placeholder="All"
              />
            </View>
            <View style={styles.compactField}>
              <Text style={styles.compactFieldLabel}>3PL</Text>
              <LocationFilterDropdown
                label="3PL"
                options={tplOptions}
                value={selectedTplObjects.length > 0 ? selectedTplObjects : null}
                onChange={onTplChange}
                isMulti
                showLabelInTrigger={false}
                size="compact"
                placeholder="All"
              />
            </View>
            <View style={styles.compactField}>
              <Text style={styles.compactFieldLabel}>SIZE</Text>
              <LocationFilterDropdown
                label="Size"
                options={sizeOptions}
                value={selectedSizeObjects.length > 0 ? selectedSizeObjects : null}
                onChange={onSizeChange}
                isMulti
                showLabelInTrigger={false}
                size="compact"
                placeholder="All"
              />
            </View>
            <View style={styles.compactField}>
              <Text style={styles.compactFieldLabel}>STATUS</Text>
              <LocationFilterDropdown
                label="Status"
                options={statusOptions}
                value={selectedStatusObjects.length > 0 ? selectedStatusObjects : null}
                onChange={handleStatusChange}
                isMulti
                showLabelInTrigger={false}
                size="compact"
                placeholder="All"
              />
            </View>
          </ScrollView>

          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.compactFilterRowBottom}
          >
            <View style={styles.compactField}>
              <Text style={styles.compactFieldLabel}>REGION</Text>
              <LocationFilterDropdown
                label="Region"
                options={regionOptions}
                value={selectedRegions.length > 0 ? selectedRegions : null}
                onChange={onRegionChange}
                isMulti
                isLoading={isLoadingRegions}
                showLabelInTrigger={false}
                size="compact"
                placeholder="All"
              />
            </View>
            <View style={styles.compactField}>
              <Text style={styles.compactFieldLabel}>MARKET</Text>
              <LocationFilterDropdown
                label="Market"
                options={marketOptions}
                value={selectedMarkets.length > 0 ? selectedMarkets : null}
                onChange={onMarketChange}
                isMulti
                isLoading={isLoadingMarkets}
                showLabelInTrigger={false}
                size="compact"
                placeholder="All"
              />
            </View>
            <View style={styles.compactField}>
              <Text style={styles.compactFieldLabel}>LOCATION</Text>
              <LocationFilterDropdown
                label="Location"
                options={locationOptions}
                value={selectedLocations.length > 0 ? selectedLocations : null}
                onChange={onLocationChange}
                isMulti
                isDisabled={isLocationDisabled}
                isLoading={isLoadingLocations}
                placeholder="All"
                showLabelInTrigger={false}
                size="compact"
              />
            </View>
            <View style={styles.compactField}>
              <Text style={styles.compactFieldLabel}>LOCATION TYPE</Text>
              <LocationFilterDropdown
                label="Location Type"
                options={locationTypeOptions}
                value={selectedLocationTypes.length > 0 ? selectedLocationTypes : null}
                onChange={onLocationTypeChange}
                isMulti
                showLabelInTrigger={false}
                size="compact"
                placeholder="All"
              />
            </View>
            <View style={styles.compactField}>
              <Text style={styles.compactFieldLabel}>UNION STATUS</Text>
              <LocationFilterDropdown
                label="Union Status"
                options={unionStatusOptions}
                value={selectedUnionStatusObjects.length > 0 ? selectedUnionStatusObjects : null}
                onChange={onUnionStatusChange}
                isMulti
                showLabelInTrigger={false}
                size="compact"
                placeholder="All"
              />
            </View>
          </ScrollView>
        </View>
      );
    }

    return (
      <>
        {/* Search Bar */}
        <View style={styles.desktopSearchBar}>
          <View style={styles.searchWrap}>
            <MaterialIcons name="search" size={16} color={AdminColors.textMuted} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search locations..."
              placeholderTextColor={AdminColors.textMuted}
              value={searchValue}
              onChangeText={onSearchChange}
            />
            {searchValue && (
              <TouchableOpacity onPress={onSearchClear} style={styles.searchClear}>
                <MaterialIcons name="close" size={16} color={AdminColors.textMuted} />
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Filter Controls */}
        <View style={styles.desktopFilterRow}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.filterControls}
          >
            <LocationFilterDropdown
              label="Region"
              options={regionOptions}
              value={selectedRegions.length > 0 ? selectedRegions : null}
              onChange={onRegionChange}
              isMulti
              isLoading={isLoadingRegions}
            />
            <LocationFilterDropdown
              label="Market"
              options={marketOptions}
              value={selectedMarkets.length > 0 ? selectedMarkets : null}
              onChange={onMarketChange}
              isMulti
              isLoading={isLoadingMarkets}
            />
            <LocationFilterDropdown
              label="Location"
              options={locationOptions}
              value={selectedLocations.length > 0 ? selectedLocations : null}
              onChange={onLocationChange}
              isMulti
              isDisabled={isLocationDisabled}
              isLoading={isLoadingLocations}
              placeholder={locationPlaceholder}
            />
            <LocationFilterDropdown
              label="Location Type"
              options={locationTypeOptions}
              value={selectedLocationTypes.length > 0 ? selectedLocationTypes : null}
              onChange={onLocationTypeChange}
              isMulti
            />
            <LocationFilterDropdown
              label="Union Status"
              options={unionStatusOptions}
              value={selectedUnionStatusObjects.length > 0 ? selectedUnionStatusObjects : null}
              onChange={onUnionStatusChange}
              isMulti
            />
            <LocationFilterDropdown
              label="3PL"
              options={tplOptions}
              value={selectedTplObjects.length > 0 ? selectedTplObjects : null}
              onChange={onTplChange}
              isMulti
            />
            <LocationFilterDropdown
              label="Size"
              options={sizeOptions}
              value={selectedSizeObjects.length > 0 ? selectedSizeObjects : null}
              onChange={onSizeChange}
              isMulti
            />
            <LocationFilterDropdown
              label="Status"
              options={statusOptions}
              value={selectedStatusObjects.length > 0 ? selectedStatusObjects : null}
              onChange={handleStatusChange}
              isMulti
            />
          </ScrollView>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <TouchableOpacity
              style={styles.clearFiltersBtn}
              onPress={onClearAllFilters}
              activeOpacity={0.7}
            >
              <MaterialIcons name="clear" size={14} color={AdminColors.textSecondary} />
              <Text style={styles.clearFiltersBtnText}>Clear</Text>
            </TouchableOpacity>
          )}
        </View>
      </>
    );
  }

  // Mobile view
  return (
    <>
      {/* Search Bar */}
      <View style={styles.mobileSearchBar}>
        <View style={styles.searchWrap}>
          <MaterialIcons name="search" size={16} color={AdminColors.textMuted} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search locations..."
            placeholderTextColor={AdminColors.textMuted}
            value={searchValue}
            onChangeText={onSearchChange}
          />
          {searchValue && (
            <TouchableOpacity onPress={onSearchClear} style={styles.searchClear}>
              <MaterialIcons name="close" size={16} color={AdminColors.textMuted} />
            </TouchableOpacity>
          )}
        </View>

        {/* Filter Button */}
        <TouchableOpacity
          style={styles.mobileFilterBtn}
          onPress={() => setMobileFilterOpen(true)}
          activeOpacity={0.7}
        >
          <MaterialIcons name="filter-list" size={18} color="#2D2D2D" />
          {appliedFilterCount > 0 && (
            <View style={styles.mobileFilterBadge}>
              <Text style={styles.mobileFilterBadgeText}>{appliedFilterCount}</Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      {/* Mobile Filter Panel */}
      <LocationFilterPanel
        visible={mobileFilterOpen}
        onClose={handleMobileFilterClose}
        onApply={handleMobileFilterApply}
        onReset={handleMobileFilterReset}
        regionOptions={regionOptions}
        marketOptions={marketOptions}
        locationOptions={locationOptions}
        departmentOptions={departmentOptions}
        orgOptions={orgOptions}
        locationTypeOptions={locationTypeOptions}
        selectedRegions={selectedRegions}
        onRegionChange={onRegionChange}
        selectedMarkets={selectedMarkets}
        onMarketChange={onMarketChange}
        selectedDepartments={selectedDepartments}
        onDepartmentChange={onDepartmentChange}
        selectedOrgs={selectedOrgs}
        onOrgChange={onOrgChange}
        selectedTpl={selectedTpl}
        onTplChange={onTplChange}
        selectedUnionStatuses={selectedUnionStatuses}
        onUnionStatusChange={onUnionStatusChange}
        selectedSizes={selectedSizes}
        onSizeChange={onSizeChange}
        selectedLocations={selectedLocations}
        onLocationChange={onLocationChange}
        selectedLocationTypes={selectedLocationTypes}
        onLocationTypeChange={onLocationTypeChange}
        selectedStatuses={selectedStatuses}
        onStatusChange={onStatusChange}
        isLocationDisabled={isLocationDisabled}
        locationPlaceholder={locationPlaceholder}
        isLoadingRegions={isLoadingRegions}
        isLoadingMarkets={isLoadingMarkets}
        isLoadingLocations={isLoadingLocations}
      />
    </>
  );
};

const styles = StyleSheet.create({
  // Desktop layout
  desktopSearchBar: {
    marginBottom: 12,
  },
  desktopFilterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 12,
  },
  filterControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingRight: 8,
  },
  clearFiltersBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
    minHeight: 36,
    flexShrink: 0,
  },
  clearFiltersBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },

  // Mobile layout
  mobileSearchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  searchWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    paddingHorizontal: 10,
    height: 36,
    backgroundColor: AdminColors.bgSurface,
  },
  searchInput: {
    flex: 1,
    fontSize: 12,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    padding: 0,
  },
  searchClear: {
    padding: 4,
  },
  mobileFilterBtn: {
    width: 36,
    height: 36,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: AdminColors.bgSurface,
  },
  mobileFilterBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    backgroundColor: AdminColors.blue700,
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileFilterBadgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  compactFilterControls: {
    gap: 6,
  },
  compactFilterRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingHorizontal: 0,
  },
  compactFilterRowBottom: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingHorizontal: 0,
    marginTop: 8,
  },
  compactField: {
    gap: 6,
    flexShrink: 0,
  },
  compactFieldLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
    letterSpacing: 0.6,
  },
});

export default LocationFilterBar;
